/**
 * DB PageStyleConfig -> UI AvatarPageConfig 변환 유틸리티
 *
 * PageStyleConfig (snake_case): DB/API에서 사용하는 형식
 * AvatarPageConfig (camelCase): UI 컴포넌트에서 사용하는 형식
 */

import type { PageStyleConfig, IntroConfig } from '@/widgets/agent-intro-config/types/intro-config';
import type { AvatarPageConfig } from '../types/avatar-page-config';
import { DEFAULT_AVATAR_CONFIG } from '../constants/default-avatar-config';
import { getGradientPresetById, getDefaultGradient } from '../constants/presets';

/**
 * PageStyleConfig를 AvatarPageConfig로 변환
 */
export function transformPageStyleToAvatarConfig(
  pageStyle: PageStyleConfig | undefined,
  intro?: IntroConfig
): Partial<AvatarPageConfig> {
  if (!pageStyle) return {};

  const result: Partial<AvatarPageConfig> = {};

  // 배경 타입 결정
  const backgroundType = pageStyle.background_type || 'gradient';
  const backgroundImageUrl = pageStyle.background_image_url;

  // 배경 그라데이션 결정 (커스텀 그라데이션 우선, 없으면 프리셋 ID)
  const backgroundGradient = pageStyle.use_custom_gradient && pageStyle.custom_gradient
    ? pageStyle.custom_gradient
    : pageStyle.gradient_preset_id
      ? getGradientPresetById(pageStyle.gradient_preset_id)?.gradient || pageStyle.background_gradient
      : pageStyle.background_gradient || getDefaultGradient();

  // ConsentModal 변환
  if (pageStyle.consent_modal) {
    const cm = pageStyle.consent_modal;
    result.consentModal = {
      title: cm.title || intro?.ko?.title || DEFAULT_AVATAR_CONFIG.consentModal?.title || '',
      description: cm.description || intro?.ko?.description || DEFAULT_AVATAR_CONFIG.consentModal?.description || '',
      background: backgroundGradient,
      image: cm.modal_image_url,
      videoUrl: cm.video_url,
      buttonText: cm.button_text || '동의 후 대화하기',
    };
  } else {
    // consent_modal이 없어도 배경은 적용
    result.consentModal = {
      ...DEFAULT_AVATAR_CONFIG.consentModal!,
      title: intro?.ko?.title || DEFAULT_AVATAR_CONFIG.consentModal?.title || '',
      description: intro?.ko?.description || DEFAULT_AVATAR_CONFIG.consentModal?.description || '',
      background: backgroundGradient,
    };
  }

  // InputControls 변환 (텍스트는 항상 i18n으로 처리 - 언어 선택 시 자동 번역 지원)
  if (pageStyle.input_control) {
    const ic = pageStyle.input_control;
    result.inputControls = {
      blurStrength: ic.blur_intensity ?? 20,
      // placeholder, voiceButtonText, chatButtonText는 i18n에서 처리
      // DB 값을 무시하고 InputControls 컴포넌트에서 언어별 번역 사용
    };
  }

  // ChatOverlay 변환
  result.chatOverlay = {
    background: backgroundGradient,
    backgroundType: backgroundType,
    backgroundImageUrl: backgroundImageUrl,
    styleConfig: {
      aiProfileImage: pageStyle.consent_modal?.profile_image_url ||
        DEFAULT_AVATAR_CONFIG.chatOverlay?.styleConfig?.aiProfileImage || '',
      messages: pageStyle.chat_bubble
        ? {
            botBubble: {
              backgroundColor: pageStyle.chat_bubble.ai_background_color,
              textColor: pageStyle.chat_bubble.ai_text_color,
              borderRadius: 20,
              padding: 12,
              fontSize: 14,
              fontWeight: '500',
            },
            userBubble: {
              backgroundColor: pageStyle.chat_bubble.user_background_color,
              textColor: pageStyle.chat_bubble.user_text_color,
              borderRadius: 20,
              padding: 12,
              fontSize: 14,
              fontWeight: '500',
            },
            spacing: 16,
          }
        : DEFAULT_AVATAR_CONFIG.chatOverlay?.styleConfig?.messages!,
    },
  };

  return result;
}

/**
 * agent.config 객체에서 페이지 설정 추출
 */
export function extractPageConfigFromAgent(agentConfig: any): {
  pageStyle?: PageStyleConfig;
  intro?: IntroConfig;
} {
  return {
    pageStyle: agentConfig?.page_style as PageStyleConfig | undefined,
    intro: agentConfig?.intro as IntroConfig | undefined,
  };
}
